import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui';
import home from './Home.module.css';
import styles from './HomeKpiTile.module.css';

export type HomeKpiTone = 'default' | 'ok' | 'alert' | 'unavailable';

/**
 * EAA-1814 (S3 rebuild — canonical frame 09 "Inset shell home"): one tile in
 * the home dashboard's KPI row.
 *
 * Rebuilt from the S2 shape: frame 09's tile carries a 40x40 icon circle at
 * its top-right (line ~460-467 of the frame dump — plain ring by default,
 * a red ring+fill for the degraded 4th tile), which the S2 tile had no slot
 * for. `value` now accepts a `ReactNode` rather than only a number so the
 * "Ứng dụng đang chạy" tile can render the frame's composite
 * "{enabled} <small>/ {total}</small>" typography (frame lines 517-527) without
 * a second bespoke component.
 *
 * `tone="unavailable"` (dashed ring, muted, "—", no skeleton — because there
 * is nothing to load) is preserved as-is from S2: still the only honest way
 * to render a value the endpoint genuinely does not return.
 */
export function HomeKpiTile({
    label,
    value,
    sub,
    tone = 'default',
    icon,
    to,
    loading = false,
    unavailableNote,
    errorNote,
    onRetry,
}: {
    label: string;
    value?: ReactNode;
    sub?: ReactNode;
    tone?: HomeKpiTone;
    /** The frame's top-right icon circle. Decorative — the label already
     *  names the metric. */
    icon?: ReactNode;
    to?: string;
    loading?: boolean;
    /** Rendered instead of `value` when `tone === 'unavailable'`. */
    unavailableNote?: ReactNode;
    /** Fail-soft per tile (mirrors AppCard's per-card degrade) — set instead of `value`. */
    errorNote?: ReactNode;
    onRetry?: () => void;
}) {
    const { t } = useTranslation();
    // EAA-1846: the tone modifiers used to be the ambient global strings
    // `is-ok`/`is-alert`/`is-unavailable`, which a dozen other features also
    // define; they are hashed module classes now, so the tile can only be
    // restyled from its own module.
    const className =
        styles.tile +
        (tone === 'ok' ? ' ' + styles.toneOk : '') +
        (tone === 'alert' ? ' ' + styles.toneAlert : '') +
        (tone === 'unavailable' ? ' ' + styles.toneUnavailable : '');
    const iconClassName = styles.icon + (tone === 'alert' ? ' ' + styles.iconAlert : '');

    // Real row height while loading — no layout shift once data lands.
    if (loading) {
        return (
            <div className={className} role="status" aria-live="polite">
                <span className="portal-sr-only">{t('home.loadingSr')}</span>
                <div className={styles.top}>
                    <Skeleton width="60%" height="0.85rem" />
                    <Skeleton width="2.5rem" height="2.5rem" />
                </div>
                <Skeleton width="40%" height="1.9rem" />
                <Skeleton width="80%" height="0.8rem" />
            </div>
        );
    }

    const content = (
        <>
            <div className={styles.top}>
                <span className={styles.label}>{label}</span>
                {icon && (
                    <span className={iconClassName} aria-hidden="true">
                        {icon}
                    </span>
                )}
            </div>
            {tone === 'unavailable' ? (
                <>
                    <span className={`${styles.value} ${styles.valueDash}`}>—</span>
                    <span className={styles.unavailableNote}>{unavailableNote}</span>
                </>
            ) : errorNote ? (
                <span className={styles.error}>
                    {errorNote}
                    {onRetry && (
                        <button
                            type="button"
                            className={home.retry}
                            onClick={(event) => {
                                event.preventDefault();
                                onRetry();
                            }}
                        >
                            {t('home.retry')}
                        </button>
                    )}
                </span>
            ) : (
                <>
                    <span className={styles.value}>{value}</span>
                    {sub !== undefined && <span className={styles.sub}>{sub}</span>}
                </>
            )}
        </>
    );

    if (to && tone !== 'unavailable' && !errorNote) {
        return (
            <Link to={to} className={className}>
                {content}
            </Link>
        );
    }
    return <div className={className}>{content}</div>;
}

export default HomeKpiTile;
